// src/views/Users/Customers/Modal/AddProjectModal.jsx
import React, { useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { candidateActions } from "../../redux/actions";
import { ToastifyService } from "../../services";
import { getUserData } from "../../system";

export default function UploadExcelModal({ show, onHide, isRecruiter=false }) {
  const dispatch = useDispatch();
  const inputRef = useRef(null);

  const [excelFile, setExcelFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const projectData = useSelector((s) => s.projectReducer.formData);
  const decryptedData = getUserData();
  const isExcel = (name = "") => /\.(xlsx|xls)$/i.test(name);

  const formatBytes = (bytes) => {
    if (bytes == null) return "";
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.min(
      Math.floor(Math.log(bytes) / Math.log(1024)),
      sizes.length - 1
    );
    const val = bytes / Math.pow(1024, i);
    return `${val.toFixed(val >= 100 ? 0 : val >= 10 ? 1 : 2)} ${sizes[i]}`;
  };

  const pickFile = () => inputRef.current?.click();

  const onInputChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!isExcel(f.name)) {
      ToastifyService.showError("Only .xlsx or .xls files are allowed.");
      e.target.value = "";
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      ToastifyService.showError("Max file size is 20MB.");
      e.target.value = "";
      return;
    }
    setExcelFile(f);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (!f) return;
    if (!isExcel(f.name)) {
      ToastifyService.showError("Only .xlsx or .xls files are allowed.");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      ToastifyService.showError("Max file size is 20MB.");
      return;
    }
    setExcelFile(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!excelFile) {
      ToastifyService.showError("Please choose an Excel file first.");
      return;
    }

    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append("File", excelFile, excelFile.name);
      if (isRecruiter) {
        fd.append("ProjectId", projectData.id);
        fd.append("RecruiterId", decryptedData.id);
      }
      // Implement this action in your redux layer to call the .NET import API:
      // e.g., POST /api/candidates/import
      dispatch(candidateActions.uploadCandidateExcel(fd));
      onHide();
    } catch (err) {
      ToastifyService.showError(
        err?.message || "Upload failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="md" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Upload Excel</Modal.Title>
      </Modal.Header>

      <form onSubmit={onSubmit}>
        <Modal.Body>
          {/* Optional: Template download */}
          {/* <div className="mb-3 d-flex justify-content-end">
            <a
              href={process.env.REACT_APP_STORAGE_URL + "template.xlsx"}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-success d-flex align-items-center"
            >
              <i className="bi bi-download me-2"></i>Download Template
            </a>
          </div> */}

          {/* Hidden input for click-to-browse */}
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls"
            className="d-none"
            onChange={onInputChange}
            disabled={submitting}
          />

          {/* Dropzone */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => !submitting && pickFile()}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && !submitting)
                pickFile();
            }}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            className="p-4 text-center"
            style={{
              border: "2px dashed",
              borderColor: dragOver ? "#2e6bff" : "#ced4da",
              borderRadius: 12,
              background: dragOver ? "rgba(46,107,255,0.06)" : "#f8f9fb",
              transition: "all .15s ease",
              cursor: submitting ? "not-allowed" : "pointer",
              userSelect: "none",
            }}
          >
            <div className="d-flex flex-column align-items-center">
              <i
                className={`bi ${
                  excelFile
                    ? "bi-file-earmark-excel"
                    : "bi-file-earmark-arrow-up"
                } mb-2`}
                style={{
                  fontSize: 32,
                  color: dragOver ? "#2e6bff" : "#6c757d",
                }}
              />
              <div className="fw-semibold" style={{ fontSize: 15 }}>
                {excelFile ? "File selected" : "Drag & drop Excel here"}
              </div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                or{" "}
                <span className="text-primary text-decoration-underline">
                  browse
                </span>{" "}
                to choose a file
              </div>
              <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                Accepted: .xlsx, .xls • Max 20MB
              </div>
              {excelFile && (
                <div className="mt-2 small">
                  {excelFile.name} • {formatBytes(excelFile.size)}
                </div>
              )}
            </div>
          </div>

          {/* Status + Clear */}
          <div className="d-flex align-items-center justify-content-between mt-2">
            <small className="text-muted">
              {excelFile ? "Ready to upload" : "No file selected"}
            </small>
            {excelFile && (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => {
                  setExcelFile(null);
                  if (inputRef.current) inputRef.current.value = "";
                }}
                disabled={submitting}
              >
                Remove
              </button>
            )}
          </div>
          <div className="d-flex justify-content-end gap-2 mt-5">
            <Button type="submit" variant="success" className="btn-footer">
              Upload
            </Button>
          </div>
        </Modal.Body>

        {/* <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!excelFile || submitting}>
            {submitting ? "Uploading..." : "Upload"}
          </Button>
        </Modal.Footer> */}
      </form>
    </Modal>
  );
}
